import { useState, useRef, useEffect } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useNavigate } from "react-router-dom";
import Testimonials from "./Testimonials.jsx";
import "../styles/home.css";

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState(null);
    const heroRef = useRef();
    const carouselRef = useRef();

    /* ── hero image carousel ── */
    const heroImages = [
        "/assets/homebg1.jpg",
        "/assets/makeup/bride.jpg",
        "/assets/makeup/facial.jpg",
        "/assets/imagess/model1.jpg",
    ];
    const [heroIdx, setHeroIdx] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setHeroIdx(p => (p + 1) % heroImages.length), 4500);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const ctx = gsap.context(() => {
            gsap.from(".hero-line", {
                yPercent: 110, opacity: 0, duration: 1.2,
                stagger: 0.15, ease: "expo.out", delay: 0.3
            });
            gsap.from(".hero-sub", { opacity: 0, y: 20, duration: 1, delay: 0.9, ease: "power3.out" });
            gsap.from(".hero-cta", { opacity: 0, y: 20, duration: 1, delay: 1.1, ease: "power3.out" });
            /* reveal-up */
            gsap.utils.toArray(".reveal-up").forEach(el => {
                gsap.from(el, {
                    scrollTrigger: { trigger: el, start: "top 87%", toggleActions: "play none none none" },
                    y: 60, opacity: 0, duration: 0.9, ease: "power3.out"
                });
            });
            /* stat counters */
            gsap.utils.toArray(".stat-num").forEach(el => {
                const target = parseInt(el.getAttribute("data-val"));
                ScrollTrigger.create({
                    trigger: el, start: "top 85%", once: true,
                    onEnter: () => {
                        gsap.to({ val: 0 }, {
                            val: target, duration: 2, ease: "power2.out",
                            onUpdate: function () { el.textContent = Math.round(this.targets()[0].val) + "+"; }
                        });
                    }
                });
            });
            /* marquee */
            const track = document.querySelector(".marquee-track");
            if (track) gsap.to(track, { xPercent: -50, duration: 22, ease: "none", repeat: -1 });
            /* services image parallax */
            gsap.from(".about-image-wrap", {
                scrollTrigger: { trigger: ".about-section", start: "top 80%", end: "bottom 20%", scrub: 1 },
                yPercent: -15
            });
        }, heroRef);
        return () => ctx.revert();
    }, []);

    const services = [
        { title: "Hair Styling", tag: "01", description: "Transform your look with precision cuts and expert styling tailored to your unique features and lifestyle.", image: "/assets/imagess/haircut.webp" },
        { title: "Bridal Makeup", tag: "02", description: "Radiate unforgettable elegance on your wedding day with our premium bridal artistry and bespoke packages.", image: "/assets/aboutimg/Bride21.webp" },
        { title: "Skin Care", tag: "03", description: "Reveal your skin's natural glow through advanced treatments, facials, and rejuvenating rituals.", image: "/assets/Home/Home/skincare.jpg" },
        { title: "Beard Grooming", tag: "04", description: "Sharp, defined, immaculate. Precision beard shaping and grooming crafted for the modern man.", image: "/assets/Home/Home/beard.jpg" },
        {
            title: "Threading",
            tag: "05",
            description: "Achieve perfectly shaped brows and smooth skin with our precise and gentle threading services for eyebrows, upper lips, and facial hair.",
            image: "/assets/Home/Home/thread.jpg"
        },
        {
            title: "Waxing",
            tag: "06",
            description: "Enjoy long-lasting smoothness with our professional waxing services that remove unwanted hair gently while keeping your skin soft and flawless.",
            image: "/assets/Home/Home/wax.jpg"
        },
        {
            title: "Facial",
            tag: "07",
            description: "Rejuvenate your skin with our nourishing facials designed to cleanse, hydrate, and restore your natural glow for healthier, radiant skin.",
            image: "/assets/Home/Home/facial.jpg"
        },
        {
            title: "Bleach & D-Tan",
            tag: "08",
            description: "Brighten and even out your complexion with our bleach and de-tan treatments that reduce tanning and restore your skin's natural radiance.",
            image: "/assets/Home/Home/bleach.jpg"
        },
        {
            title: "Massage",
            tag: "09",
            description: "Relax your body and mind with our soothing massage therapies designed to relieve stress, improve circulation, and restore your natural balance.",
            image: "/assets/Home/Home/massage.jpg"
        },
        {
            title: "Cleanup",
            tag: "10",
            description: "Refresh and purify your skin with our gentle cleanup treatment that removes impurities, unclogs pores, and leaves your face feeling clean and revitalized.",
            image: "/assets/Home/Home/cleanup.jpg"
        },
        {
            title: "Hair Spa",
            tag: "11",
            description: "Nourish and strengthen your hair with our deep conditioning hair spa treatments that repair damage, reduce dryness, and restore natural shine.",
            image: "/assets/imagess/hairspa1.jpg.jpeg"
        },
        {
            title: "Shahanaz",
            tag: "12",
            description: "Experience premium skincare with our Shahnaz facial treatments that use herbal formulations to rejuvenate skin, improve texture, and enhance natural radiance.",
            image: "/assets/Home/Home/shahanaz.jpg"
        }
    ];

    const marqueeItems = ["Hair Styling", "✦", "Bridal Makeup", "✦", "Skin Care", "✦", "Beard Grooming", "✦", "Hair Treatments", "✦", "Facials", "✦", "Tan Removal", "✦", "Nail Art", "✦"];

    const faqs = [
        { q: "How can I book an appointment?", a: "Book directly through our website by clicking 'Book a Slot', call us, or visit the salon. Our team will schedule your preferred service, date, and time with ease." },
        { q: "How often do you update your services menu?", a: "We review and refresh our offerings seasonally, integrating the latest beauty trends, techniques, and premium products as they become available." },
        { q: "How can I change or cancel my booking?", a: "Please contact us at least 24 hours before your appointment via phone or your booking platform so we can reschedule at your convenience." },
        { q: "What special packages or offers are available?", a: "We offer curated bridal packages, seasonal promotions, and combo deals. Check our website or call the salon to discover our latest exclusive offers." },
    ];

    const stats = [
        { val: 5000, label: "Happy Clients" },
        { val: 12, label: "Expert Stylists" },
        { val: 8, label: "Years of Artistry" },
        { val: 50, label: "Services Offered" },
    ];

    const galleryImages = [
        "/assets/reviews/1.jpeg", "/assets/reviews/2.jpeg", "/assets/reviews/3.jpeg",
        "/assets/reviews/4.jpeg", "/assets/reviews/5.jpeg", "/assets/reviews/6.jpeg",
        "/assets/reviews/7.jpeg", "/assets/reviews/8.jpeg",
    ];

    const whyUs = [
        { icon: "✂", title: "Expert Team", body: "Not just stylists — your trusted beauty partners, always." },
        { icon: "♡", title: "Personal Care", body: "Every client deserves thoughtful, tailor-made attention." },
        { icon: "◈", title: "Premium Products", body: "Gentle on you, powerful on results — always curated." },
        { icon: "✦", title: "Curated Space", body: "A relaxing retreat designed for peace & perfected for pampering." },
    ];

    const serviceCards = [...services];

    return (
        <div className="kq-root" ref={heroRef}>

            {/* ══════════════ HERO ══════════════ */}
            <section className="kq-hero">
                {/* background slides — hero images use eager loading since they're above the fold */}
                {heroImages.map((img, i) => (
                    <div key={i} className={`kq-hero-slide ${i === heroIdx ? "active" : ""}`}>
                        <img
                            src={img}
                            alt="salon"
                            loading={i === 0 ? "eager" : "lazy"}
                            decoding={i === 0 ? "sync" : "async"}
                        />
                    </div>
                ))}
                <div className="kq-hero-overlay" />

                <div className="kq-hero-content">
                    <p className="kq-hero-eyebrow hero-sub">Unisex Family Salon & Bridal Studio</p>
                    <div className="kq-hero-title">
                        <div className="kq-title-clip"><span className="hero-line">King &</span></div>
                        <div className="kq-title-clip"><span className="hero-line kq-italic">Queen</span></div>
                    </div>
                    <p className="kq-hero-tagline hero-sub">Where artistry meets elegance — your beauty journey begins here.</p>
                    <div className="hero-cta kq-hero-btns">
                        <button className="kq-btn-primary" onClick={() => navigate("/contact")}>Book an Appointment</button>
                        <button className="kq-btn-ghost" onClick={() => navigate("/services")}>Our Services</button>
                    </div>
                </div>

                {/* slide dots */}
                <div className="kq-hero-dots">
                    {heroImages.map((_, i) => (
                        <button key={i} className={`kq-dot ${i === heroIdx ? "active" : ""}`} onClick={() => setHeroIdx(i)} />
                    ))}
                </div>

                <div className="kq-hero-scroll-hint">
                    <span>Scroll</span>
                    <div className="kq-scroll-line" />
                </div>
            </section>

            {/* ══════════════ MARQUEE ══════════════ */}
            <div className="kq-marquee">
                <div className="marquee-track">
                    {[...marqueeItems, ...marqueeItems].map((item, i) => (
                        <span key={i} className={item === "✦" ? "kq-marquee-dot" : "kq-marquee-item"}>{item}</span>
                    ))}
                </div>
            </div>

            {/* ══════════════ SERVICES CAROUSEL ══════════════ */}
            <section className="kq-svc-scroll-section">
                <div className="kq-svc-scroll-header reveal-up">
                    <p className="kq-section-eyebrow">Experience Our Services</p>
                    <h2 className="kq-section-title">What We <em>Offer</em></h2>
                </div>
                <div className="kq-svc-carousel" ref={carouselRef}>
                    {serviceCards.map((svc, i) => (
                        <div key={i} className="kq-svc-card" onClick={() => navigate("/services")}>
                            <div className="kq-svc-card-img">
                                <img
                                    src={svc.image}
                                    alt={svc.title}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="kq-svc-card-label">
                                <span className="kq-svc-card-tag">{svc.tag || `0${(i % 4) + 1}`}</span>
                                <span className="kq-svc-card-name">{svc.title}</span>
                            </div>
                            <div className="kq-svc-card-hover">
                                <p>{svc.description}</p>
                                <span className="kq-svc-card-cta">Explore →</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ══════════════ ABOUT ══════════════ */}
            <section className="kq-about about-section">
                <div className="kq-about-image-col">
                    <div className="about-image-wrap">
                        <img
                            src="/assets/aboutimg.webp"
                            alt="salon interior"
                            loading="lazy"
                            decoding="async"
                        />
                        <div className="kq-about-badge reveal-up">
                            <span className="kq-badge-num">8</span>
                            <span className="kq-badge-label">Years of<br />Excellence</span>
                        </div>
                    </div>
                </div>
                <div className="kq-about-text-col">
                    <p className="kq-section-eyebrow reveal-up">Our Story</p>
                    <h2 className="kq-section-title reveal-up">Welcome to<br /><em>King & Queen</em></h2>
                    <p className="kq-about-body reveal-up">
                        At King & Queen, beauty is not just a service — it's an experience. Our team of passionate artists blends technical precision with creative flair to craft looks that celebrate your individuality. From everyday elegance to once-in-a-lifetime bridal moments, we're here for every chapter of your story.
                    </p>
                    <div className="kq-about-stats reveal-up">
                        {stats.map((s, i) => (
                            <div key={i} className="kq-stat">
                                <span className="stat-num kq-stat-num" data-val={s.val}>0+</span>
                                <span className="kq-stat-label">{s.label}</span>
                            </div>
                        ))}
                    </div>
                    <button className="kq-btn-primary reveal-up" onClick={() => navigate("/contact")}>Reserve Your Slot</button>
                </div>
            </section>

            {/* ══════════════ WHY US ══════════════ */}
            <section className="kq-why">
                <div className="kq-why-header reveal-up">
                    <p className="kq-section-eyebrow">Why Choose Us</p>
                    <h2 className="kq-section-title">Why <em>King & Queen</em></h2>
                </div>
                <div className="kq-why-grid">
                    {whyUs.map((w, i) => (
                        <div key={i} className="kq-why-card reveal-up">
                            <span className="kq-why-icon">{w.icon}</span>
                            <h3 className="kq-why-title">{w.title}</h3>
                            <p className="kq-why-body">{w.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ══════════════ GALLERY ══════════════ */}
            <section className="kq-gallery">
                <div className="kq-gallery-header reveal-up">
                    <p className="kq-section-eyebrow">Our Work</p>
                    <h2 className="kq-section-title">Moments of <em>Us</em></h2>
                    <p className="kq-gallery-sub">Here are all we have for the beautiful transformations</p>
                    <button className="kq-btn-primary" onClick={() => navigate("/contact")}>Book Now</button>
                </div>

                <div className="kq-gallery-scroll-wrap">
                    <div className="kq-gallery-row kq-gallery-row-left">
                        {[...galleryImages, ...galleryImages].map((img, i) => (
                            <div key={i} className="kq-gallery-portrait">
                                <img
                                    src={img}
                                    alt={`transformation ${i + 1}`}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div className="kq-gallery-hover"><span>View</span></div>
                            </div>
                        ))}
                    </div>
                    <div className="kq-gallery-row kq-gallery-row-right">
                        {[...galleryImages.slice(4), ...galleryImages, ...galleryImages.slice(0, 4)].map((img, i) => (
                            <div key={i} className="kq-gallery-portrait">
                                <img
                                    src={img}
                                    alt={`transformation ${i + 1}`}
                                    loading="lazy"
                                    decoding="async"
                                />
                                <div className="kq-gallery-hover"><span>View</span></div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══════════════ TESTIMONIALS ══════════════ */}
            <Testimonials />

            {/* ══════════════ CTA BANNER ══════════════ */}
            <section className="kq-cta-banner">
                <div className="kq-cta-banner-bg">
                    <img
                        src="/assets/imagess/girl.jpg"
                        alt="bridal"
                        loading="lazy"
                        decoding="async"
                    />
                    <div className="kq-cta-banner-overlay" />
                </div>
                <div className="kq-cta-banner-content reveal-up">
                    <p className="kq-section-eyebrow light">The Art of Caring</p>
                    <h2 className="kq-section-title light">An exceptional salon<br /><em>experience awaits you</em></h2>
                    <p className="kq-cta-banner-body">
                        Connect with us today and step into a salon experience that's uniquely crafted just for you.
                    </p>
                    <button className="kq-btn-gold" onClick={() => navigate("/contact")}>Book Your Experience</button>
                </div>
            </section>

            {/* ══════════════ INSTAGRAM STRIP ══════════════ */}
            <section className="kq-insta">
                <div className="kq-insta-header reveal-up">
                    <p className="kq-section-eyebrow">Follow Along</p>
                    <h2 className="kq-section-title">Visit <em> <a href="https://www.instagram.com/king_queen_pkd/"> Instagram </a></em></h2>
                </div>
                <div className="kq-insta-strip">
                    {[...galleryImages, ...galleryImages].map((img, i) => (
                        <div key={i} className="kq-insta-item">
                            <img
                                src={img}
                                alt="instagram"
                                loading="lazy"
                                decoding="async"
                            />
                            <div className="kq-insta-hover">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <rect x="2" y="2" width="20" height="20" rx="5" stroke="white" strokeWidth="1.5" />
                                    <circle cx="12" cy="12" r="4" stroke="white" strokeWidth="1.5" />
                                    <circle cx="17.5" cy="6.5" r="1" fill="white" />
                                </svg>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* ══════════════ FAQ ══════════════ */}
            <section className="kq-faq">
                <div className="reveal-up">
                    <p className="kq-section-eyebrow">Got Questions?</p>
                    <h2 className="kq-section-title">Frequently <em>Asked</em></h2>
                </div>
                <div className="kq-faq-list">
                    {faqs.map((faq, i) => (
                        <div key={i} className={`kq-faq-item ${openFaq === i ? "open" : ""}`}>
                            <button className="kq-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                <span>{faq.q}</span>
                                <span className="kq-faq-icon">{openFaq === i ? "−" : "+"}</span>
                            </button>
                            <div className="kq-faq-a"><p>{faq.a}</p></div>
                        </div>
                    ))}
                </div>
            </section>

        </div>
    );
};

export default Home;